import { Injectable } from '@angular/core';
import { ApplicationConfig } from '@config/application.config';
import { AuthProvider } from '@data/auth/AuthProvider';
import { GoogleAuthResponseDTO } from '@data/auth/GoogleAuthResponseDTO';
import { TokenDTO } from '@data/auth/TokenDTO';
import { Store } from '@ngxs/store';
import { LoaderActions } from '@shared/store/loader/loader.actions';
import { HttpUtil } from '@shared/util/http.util';
import { CognitoRefreshToken, CognitoUser, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { environment } from 'environments/environment';
import { finalize, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class CognitoAuthProvider implements AuthProvider {
  private cognitoUser: CognitoUser;

  constructor(private readonly store: Store, private httpUtil: HttpUtil) {
  }

  public login(code: string, email: string = ''): Observable<TokenDTO> {
    const tokenUrl = `${environment.apiUrl}/accounts:sso-login`;
    const redirectUrl = `${environment.environmentUrl}/auth/login`

    return this.httpUtil.post(tokenUrl, { code, redirectUrl }).pipe(map((res: GoogleAuthResponseDTO) => {
      return this.buildGoogleToken(res, email)
    }))
  }

  public refresh(token: TokenDTO): Observable<TokenDTO> {
    return new Observable<TokenDTO>(observer => {
      this.cognitoUser = this.getCognitoUser(token.email);
      this.cognitoUser?.refreshSession(
        new CognitoRefreshToken({ RefreshToken: token.refreshToken }),
        (error: any, session: CognitoUserSession) => {
          if (!error) {
            observer.next(this.buildToken(session, token.email));
            observer.complete();
          } else {
            observer.error(error);
          }
        });
    });
  }

  public updatePassword(oldPassword: string, newPassword): Observable<void> {
    this.setLoader(true, 'updatePassword');
    return new Observable<void>(observer => {
      this.cognitoUser.changePassword(oldPassword, newPassword, (error: any, status: 'SUCCESS') => {
        if (!error) {
          observer.next();
          observer.complete();
        } else {
          observer.error(error);
        }
      });
    })
      .pipe(
        finalize(() => this.setLoader(false, 'updatePassword'))
      );
  }

  private getCognitoUser(googleIdToken: string): CognitoUser {
    return new CognitoUser({
      Username: googleIdToken,
      Pool: this.getPool()
    });
  }

  private getPool(): CognitoUserPool {
    return new CognitoUserPool({
      UserPoolId: ApplicationConfig.cognito.userPoolId,
      ClientId: ApplicationConfig.cognito.userPoolWebClientId
    });
  }

  private buildToken(session: CognitoUserSession, email: string): TokenDTO {
    const token: TokenDTO = new TokenDTO();

    token.accessToken = session.getIdToken().getJwtToken();
    token.refreshToken = session.getRefreshToken().getToken();
    token.email = email;

    return token;
  }

  private buildGoogleToken(googleToken: GoogleAuthResponseDTO, email: string): TokenDTO {
    const token: TokenDTO = new TokenDTO();
    token.accessToken = googleToken.id_token;
    token.refreshToken = googleToken.refresh_token;
    token.email = email;

    return token;
  }

  private setLoader(value: boolean, key: string): void {
    const action = value ? new LoaderActions.StartLoading(key) : new LoaderActions.StopLoading(key);

    this.store.dispatch(action);
  }
}

